import { ComponentPropsWithRef, FC, ReactNode, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button, Modal, Typography } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Box, CircularProgress, Stack } from '@mui/material';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import { Check } from '@mui/icons-material';
import { Job, useJobStatus } from 'store/jobs';
import { SystemColors } from 'melp-design/style';
import { formatPercents } from 'utils/format';

type ModalAction = ComponentPropsWithRef<typeof Button>;

export interface JobStatusModalProps extends ModalProps {
  jobId: string;
  queue: string;
  title: ReactNode;
  description?: ReactNode;
  refetchInterval?: number;
  actions?: (closeModal: ModalProps['closeModal']) => ModalAction[];
  onSuccess: (job: Job, closeModal: ModalProps['closeModal']) => void;
  onError?: (job: Job, closeModal: ModalProps['closeModal']) => void;
}

const PROGRESS_SIZE = 120;
const TASK_DOT_SIZE = 28;
const DEFAULT_REFETCH_INTERVAL = 1000;

export const JobStatusModal: FC<JobStatusModalProps> = ({
  closeModal,
  jobId,
  queue,
  title,
  description,
  refetchInterval = DEFAULT_REFETCH_INTERVAL,
  actions,
  onSuccess,
  onError,
}) => {
  const { t, i18n } = useTranslation();
  const handledJob = useRef(false);
  const { data: job } = useJobStatus({ queue, jobId }, { refetchInterval });

  useEffect(() => {
    if (!job || handledJob.current) return;

    if (job.state === 'error') {
      handledJob.current = true;

      if (onError) {
        onError(job, closeModal);
      } else {
        toast.error(job.error || t('errors.genericErrorMessage'));
        closeModal();
      }

      return;
    }

    if (job.state !== 'success') return;

    handledJob.current = true;
    onSuccess(job, closeModal);
  }, [closeModal, job, onError, onSuccess, t]);

  return (
    <Modal.Content
      actions={
        actions?.(closeModal) ?? [
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
        ]
      }
    >
      {job ? (
        <Stack alignItems="center" gap={2} textAlign="center">
          <Box position="relative" display="inline-flex">
            <CircularProgress
              variant="determinate"
              value={job.percent}
              size={PROGRESS_SIZE}
            />
            <Box
              position="absolute"
              top={0}
              left={0}
              bottom={0}
              right={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography variant="h1">
                {formatPercents(i18n.language, job.percent / 100)}
              </Typography>
            </Box>
          </Box>

          <Typography variant="h2">{title}</Typography>

          {description ? (
            <Typography color="textSecondary">{description}</Typography>
          ) : null}

          {job.tasks.length ? (
            <Box width="100%" textAlign="left">
              <Timeline
                sx={{
                  m: 0,
                  p: 0,
                  '& .MuiTimelineItem-root': {
                    minHeight: 'auto',
                    '&:before': { flex: 0, p: 0 },
                  },
                }}
              >
                {job.tasks.map((task, index) => (
                  <TimelineItem key={index}>
                    <TimelineSeparator>
                      <TimelineDot
                        sx={{
                          m: 0,
                          width: TASK_DOT_SIZE,
                          height: TASK_DOT_SIZE,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: SystemColors.primary.base,
                          boxShadow: 'none',
                        }}
                      >
                        {task.status === 'completed' ? (
                          <Check sx={{ color: 'inherit', fontSize: 16 }} />
                        ) : (
                          <CircularProgress color="inherit" size={16} />
                        )}
                      </TimelineDot>
                      {index < job.tasks.length - 1 ? (
                        <TimelineConnector sx={{ minHeight: 10 }} />
                      ) : null}
                    </TimelineSeparator>

                    <TimelineContent>
                      <Typography variant="h3">{task.name}</Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Box>
          ) : null}
        </Stack>
      ) : (
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      )}
    </Modal.Content>
  );
};
